import React, { useState, useRef, useEffect } from 'react';

const EnhancedChipInput = ({ 
  label, 
  value = [], 
  onChange, 
  suggestions = [], 
  placeholder = "Type and press Enter to add...",
  maxVisibleSuggestions = 10,
  allowCustom = true,
  required = false,
  onAddCustom = null,
  isLoading = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPhase, setAiPhase] = useState('idle'); // idle, analyzing, generating, complete
  const [generationProgress, setGenerationProgress] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = suggestions
        .filter(suggestion => 
          suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
          !value.some(item => item.toLowerCase() === suggestion.toLowerCase())
        );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      const available = suggestions
        .filter(suggestion => 
          !value.some(item => item.toLowerCase() === suggestion.toLowerCase())
        );
      setFilteredSuggestions(available);
      setShowSuggestions(false);
    }
  }, [inputValue, suggestions, value]);

  useEffect(() => {
    if (isLoading && !isGenerating) {
      setIsGenerating(true);
      setAiPhase('analyzing');
      setGenerationProgress(0);
      
      // Immediate progress start
      let currentProgress = 0;
      const totalDuration = 2000; // 2 seconds
      const updateInterval = 50; // Update every 50ms
      const totalUpdates = totalDuration / updateInterval;
      const baseIncrement = 100 / totalUpdates;
      
      const progressInterval = setInterval(() => {
        currentProgress += baseIncrement + (Math.random() * 1 - 0.5); // Add slight randomness
        
        // Ensure we don't go over 100
        currentProgress = Math.min(currentProgress, 100);
        
        // Update phase based on progress
        if (currentProgress > 30 && currentProgress < 90 && aiPhase === 'analyzing') {
          setAiPhase('generating');
        }
        
        setGenerationProgress(currentProgress);
        
        if (currentProgress >= 100) {
          setAiPhase('complete');
          clearInterval(progressInterval);
          
          // Hide after completion
          setTimeout(() => {
            setIsGenerating(false);
            setAiPhase('idle');
            setGenerationProgress(0);
          }, 800);
        }
      }, updateInterval);
      
      return () => {
        clearInterval(progressInterval);
      };
    }
  }, [isLoading, isGenerating, aiPhase]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addItem = (item) => {
    if (item.trim() && !value.some(existing => existing.toLowerCase() === item.toLowerCase())) {
      const newValue = [...value, item.trim()];
      onChange(newValue);
      
      // Save custom suggestion if it's not in predefined suggestions
      if (onAddCustom && !suggestions.some(s => s.toLowerCase() === item.toLowerCase())) {
        onAddCustom(item.trim());
      }
      
      // Hide AI animation when user adds items
      if (isGenerating) {
        setIsGenerating(false);
        setAiPhase('idle');
        setGenerationProgress(0);
      }
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeItem = (index) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        if (allowCustom) {
          addItem(inputValue);
        } else if (filteredSuggestions.some(s => s.toLowerCase() === inputValue.toLowerCase())) {
          addItem(inputValue);
        }
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeItem(value.length - 1);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    addItem(suggestion);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    if (filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const visibleSuggestions = showAllSuggestions 
    ? filteredSuggestions 
    : filteredSuggestions.slice(0, maxVisibleSuggestions);

  const hasMoreSuggestions = filteredSuggestions.length > maxVisibleSuggestions;

  return (
    <div className="space-y-2" ref={containerRef}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        
        {/* AI Indicator with Animation */}
        {suggestions.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full shadow-sm">
            <div className="relative">
              {isGenerating ? (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                  <div className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-1 h-1 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              ) : (
                <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-ping"></div>
              )}
            </div>
            <span className="text-xs text-purple-700 font-semibold">
              {isGenerating ? (
                aiPhase === 'analyzing' ? 'AI Analyzing...' :
                aiPhase === 'generating' ? 'AI Generating...' :
                aiPhase === 'complete' ? 'AI Complete!' : 'AI Processing...'
              ) : 'AI-Enhanced'}
            </span>
          </div>
        )}
      </div>
      
      {/* Selected Items (Chips) */}
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((item, index) => (
          <span
            key={index}
            className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200 hover:from-blue-200 hover:to-indigo-200 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {item}
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>

      {/* Advanced AI Processing Animation */}
      {isGenerating && (
        <div className="relative mb-4 p-4 bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50 rounded-xl border border-purple-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {/* Neural Network Animation */}
              <div className="relative w-8 h-8">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 animate-spin" style={{animationDuration: '2s'}}></div>
                <div className="absolute inset-1 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 animate-spin" style={{animationDuration: '1.5s', animationDirection: 'reverse'}}></div>
                <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-purple-700">
                  {aiPhase === 'analyzing' && '🧠 Analyzing context and requirements...'}
                  {aiPhase === 'generating' && '⚡ Generating intelligent suggestions...'}
                  {aiPhase === 'complete' && '✨ AI processing complete!'}
                </span>
                <span className="text-xs text-purple-600 mt-1">
                  {aiPhase === 'analyzing' && 'Understanding job requirements and industry patterns'}
                  {aiPhase === 'generating' && 'Crafting personalized recommendations'}
                  {aiPhase === 'complete' && 'Ready with smart suggestions'}
                </span>
              </div>
            </div>
            
            {/* Progress Indicator */}
            <div className="text-xs font-mono text-purple-600 bg-white px-2 py-1 rounded">
              {Math.round(generationProgress)}%
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-pink-500 rounded-full transition-all duration-300 ease-out relative"
              style={{ width: `${generationProgress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
            </div>
          </div>
          
          {/* Floating Particles */}
          <div className="absolute top-2 right-4 flex space-x-1">
            <div className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
            <div className="w-1 h-1 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            <div className="w-1 h-1 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
          </div>
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />
        
        {/* Add Button */}
        <button
          type="button"
          onClick={() => {
            if (inputValue.trim()) {
              addItem(inputValue);
            }
          }}
          disabled={!inputValue.trim()}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full hover:from-blue-600 hover:to-indigo-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
          title="Add item"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>

        {/* Suggestions Dropdown */}
        {showSuggestions && visibleSuggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-80 overflow-y-auto">
            <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center gap-2 text-xs text-purple-700">
                <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-pulse"></div>
                <span className="font-medium">AI-powered intelligent suggestions</span>
              </div>
            </div>
            
            {visibleSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 focus:bg-gradient-to-r focus:from-blue-50 focus:to-indigo-50 focus:outline-none transition-all duration-200 border-b border-gray-50 last:border-b-0"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-900 font-medium">{suggestion}</span>
                  <svg className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
              </button>
            ))}
            
            {hasMoreSuggestions && !showAllSuggestions && (
              <button
                type="button"
                onClick={() => setShowAllSuggestions(true)}
                className="w-full text-left px-4 py-3 border-t border-gray-200 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 focus:bg-gradient-to-r focus:from-green-50 focus:to-emerald-50 focus:outline-none transition-all duration-200"
              >
                <div className="flex items-center gap-2 text-green-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="text-sm font-medium">Show {filteredSuggestions.length - maxVisibleSuggestions} more suggestions</span>
                </div>
              </button>
            )}
            
            {allowCustom && inputValue.trim() && !filteredSuggestions.some(s => s.toLowerCase() === inputValue.toLowerCase()) && (
              <button
                type="button"
                onClick={() => addItem(inputValue)}
                className="w-full text-left px-4 py-3 border-t border-gray-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 focus:bg-gradient-to-r focus:from-blue-50 focus:to-indigo-50 focus:outline-none transition-all duration-200"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="text-sm text-blue-600 font-medium">Add "{inputValue}"</span>
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        {allowCustom 
          ? "AI suggests the most relevant options. Type to search or add custom items. Press Enter to add."
          : "Select from AI-powered suggestions or type to search."
        }
      </p>
    </div>
  );
};

export default EnhancedChipInput;
